import { useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import ToastMsg from "../../components/ToastMsg";
import PortalPopup from "../../components/PortalPopup";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import NavigationPanel from "../../components/NavigationPanel";
import Breadcrumb from "../../components/Breadcrumb";
import CTAs from "../../components/CTAs";
import Toggle from "../../components/Toggle";
import Footer from "../../components/Footer";
import Slider from "../../components/Slider";
import SettingNav from "../../components/settingNav";
import XeroIntegration from "../../components/XeroIntegration";
import { ArrowDown2, InfoCircle } from "iconsax-reactjs";

const SettingsAIIntegrationPage = () => {
  const navigate = useNavigate();
  const [isToastMsgOpen, setToastMsgOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);

  const openToastMsg = useCallback(() => {
    setToastMsgOpen(true);
  }, []);

  const closeToastMsg = useCallback(() => {
    setToastMsgOpen(false);
  }, []);

  const closeLogoutConfirmation = useCallback(() => {
    setLogoutConfirmationOpen(false);
  }, []);

  const handleToggle = (state) => {
    console.log("Toggle state is:", state);
  };

  const thresholds = ["0", "10", "100"];

  return (
    <>
      <div className="w-full relative bg-neutrals-neutral-50 min-h-screen overflow-visible text-left text-base text-neutrals-neutral-900 font-open-sans-ff-body-strong">
        {/* Navigation Panel */}
        <NavigationPanel />

        {/* Main */}
        <div className="w-full absolute top-[136px] flex flex-col items-start justify-start gap-6 px-4 md:px-4 lg:px-24 box-border">
          {/* Heading */}
          <div className="self-stretch bg-white border-neutrals-neutral-100 border-solid border-b-[1px] flex flex-col items-start justify-start pt-0 px-0 pb-6 gap-1.5 text-9xl text-primary-primary-900">
            <b className="self-stretch relative">Settings</b>
            <div className="self-stretch relative text-base text-neutrals-neutral-300">
              Get your billing information, generate/review invoices.
            </div>
          </div>

          <div className="self-stretch flex flex-row items-start justify-start gap-8 text-base">
            {/* Tabs */}
            <SettingNav />

            {/* Right side */}
            <div className="w-full flex-1 flex flex-col items-start justify-start gap-6 text-lg text-neutrals-neutral-900">
              <div className="self-stretch border-neutrals-neutral-100 border-solid border-b-[1px] flex flex-row items-start justify-between pt-0 px-0 pb-6 gap-0">
                <div className="flex-1 flex flex-col items-start justify-start gap-1.5">
                  <div className="self-stretch relative font-semibold">
                    AI integration
                  </div>
                  <div className="self-stretch relative text-sm text-neutrals-neutral-300">
                    Manage how you use AI for keyword detection, document
                    processing, and predictive analysis.
                  </div>
                </div>
                <div className="flex flex-row items-start justify-start gap-3 text-sm">
                  <CTAs
                    type="Ghostbtn"
                    text="Cancel"
                    textColor="text-neutrals-neutral-900"
                    bgColor="bg-neutrals-neutral-50"
                    borderColor="border-neutrals-neutral-100"
                    position="relative"
                  />

                  <CTAs
                    type="Gradientbtn"
                    text="Save"
                    textColor="text-neutrals-neutral-50"
                    bgColor="bg-primary-primary-500"
                    borderColor="border-primary-primary-500"
                    position="relative"
                    onClick={openToastMsg}
                  />
                </div>
              </div>

              <div className="w-[512px] flex items-start gap-8 text-sm">
                <div className="flex-1 flex flex-col items-start gap-1">
                  <div className="self-stretch flex flex-col items-start">
                    <div className="self-stretch relative font-semibold">
                      AI status
                    </div>
                    <div className="self-stretch relative text-neutrals-neutral-300">
                      See whether the AI features are on/off.
                    </div>
                  </div>
                  <div className="self-stretch relative text-neutrals-neutral-300">
                    Last updated: 12 Oct 2025
                  </div>
                </div>
                <Toggle />
              </div>
              <div className="self-stretch flex flex-col items-start gap-6">
                <div className="self-stretch border-neutrals-neutral-100 border-solid border-b-[1px] box-border flex flex-col items-start pt-num-0 px-0 pb-5 gap-1.5">
                  <div className="self-stretch relative font-semibold">
                    Legal Document Keyword Detection
                  </div>
                  <div className="self-stretch relative text-[14px] text-slategray">
                    Configure how AI detects relevant legal or financial terms
                    in uploaded or synced documents.
                  </div>
                </div>
                <div className="self-stretch flex flex-col items-start gap-5 text-sm">
                  <div className="w-[512px] flex flex-col items-start gap-3">
                    <div className="self-stretch flex items-center gap-2">
                      <div className="flex-1 relative font-semibold">
                        Legal keywords
                      </div>
                      <Toggle />
                    </div>
                    <div className="self-stretch flex items-center gap-2">
                      <div className="flex-1 relative font-semibold">
                        Financial keywords
                      </div>
                      <Toggle />
                    </div>
                    <div className="self-stretch flex items-center gap-2">
                      <div className="flex-1 relative font-semibold">
                        Risk indicators
                      </div>
                      <Toggle />
                    </div>
                  </div>
                  <div className="self-stretch h-px relative border-neutrals-neutral-100 border-solid border-t-[1px] box-border" />
                  <div className="w-[512px] flex flex-col items-start gap-2">
                    <div className="w-[512px] flex flex-col items-start gap-1.5">
                      <div className="relative font-semibold">
                        Sensitivity level
                      </div>
                      <div className="w-[512px] first-letter:self-stretch shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] flex items-end py-3 px-2.5 text-base">
                        <div className="w-fit flex-1 relative font-semibold inline-block shrink-0">
                          Low
                        </div>
                        <ArrowDown2 size={20} />
                      </div>
                    </div>
                    <div className="self-stretch flex items-center gap-1 text-neutrals-neutral-300">
                      <InfoCircle size={16} />
                      <div className="relative">
                        Higher sensitivity captures more phrases but may
                        increase false positives.
                      </div>
                    </div>
                  </div>
                  <div className="self-stretch h-px relative border-neutrals-neutral-100 border-solid border-t-[1px] box-border" />
                  <div className="self-stretch flex flex-col items-start gap-2">
                    <Slider title="AI Confidence Threshold" />
                    {/* <div className="self-stretch h-[98px] flex flex-col items-start gap-6">
                      <div className="self-stretch relative font-semibold">
                        AI Confidence Threshold
                      </div>
                      <div className="w-[512px] h-[55px] relative text-right text-base text-slategray">
                        <div className="absolute h-2/5 w-full top-[60%] right-[0%] bottom-[0%] left-[0%] flex items-center justify-between gap-5">
                          <div className="relative text-left">0%</div>
                          <div className="relative font-semibold font-inter hidden">
                            .
                          </div>
                          <div className="w-[199px] relative inline-block shrink-0">
                            75%
                          </div>
                          <div className="relative">100%</div>
                        </div>
                        <div className="absolute h-[43.64%] w-full top-[0%] right-[0%] bottom-[56.36%] left-[0%]">
                          <div className="absolute h-[33.33%] w-full top-[33.33%] right-[0%] bottom-[33.33%] left-[0%] rounded-3xs0 bg-whitesmoke" />
                          <div className="absolute h-full w-[66.6%] top-[0%] right-[33.4%] bottom-[0%] left-[0%]">
                            <div className="absolute h-[33.33%] w-[96.77%] top-[33.33%] right-[3.23%] bottom-[33.33%] left-[0%] rounded-3xs0 bg-royalblue" />
                            <img
                              className="absolute h-full w-[7.04%] top-[0%] right-[0%] bottom-[0%] left-[92.96%] max-w-full overflow-hidden max-h-full"
                              alt=""
                            />
                          </div>
                        </div>
                      </div>
                    </div> */}
                    <div className="self-stretch flex items-center gap-1 text-neutrals-neutral-300">
                      <InfoCircle size={16} />
                      <div className="relative">
                        Controls minimum confidence for flagging a document as
                        high-risk.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="self-stretch flex flex-col items-start gap-6">
                <div className="self-stretch flex flex-col items-start gap-1.5">
                  <div className="self-stretch relative font-semibold">{`External AI & Data Integrations`}</div>
                  <div className="self-stretch relative text-sm text-neutrals-neutral-300">
                    Configure how AI detects relevant legal or financial terms
                    in uploaded or synced documents.
                  </div>
                </div>
                <div className="w-fit rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-col items-end text-base">
                  <div className="self-stretch flex flex-col items-start p-5 gap-6">
                    <div className="self-stretch flex items-start gap-2">
                      <div className="flex-1 flex items-center gap-3">
                        <img
                          className="h-[52px] w-[52px] rounded-num-8 object-cover"
                          alt=""
                        />
                        <div className="relative font-semibold">OpenAI</div>
                      </div>
                      <Toggle />
                    </div>
                    <div className="self-stretch relative text-[14px] text-neutrals-neutral-300">{`Used for smart summaries & keyword reasoning`}</div>
                  </div>
                  <div className="self-stretch border-neutrals-neutral-100 border-solid border-t-[1px] flex items-center justify-end py-4 px-6 text-sm text-primary-primary-500">
                    <div className="relative font-semibold">Reconnect</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Footer */}
          <Footer />
        </div>
      </div>
      {isToastMsgOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeToastMsg}
        >
          <ToastMsg onClose={closeToastMsg} />
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeLogoutConfirmation}
        >
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
    </>
  );
};

export default SettingsAIIntegrationPage;
